import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { User, AuthContextType } from '../types';
import { ROLE_DASHBOARD_MAP, BACKEND_ROLE_MAP } from '../constants/roles';
import { BACKEND_URL } from '../lib/backendUrl';

const API_URL = BACKEND_URL;

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

/**
 * Helper to normalize role from backend response to frontend UserRole type
 */
const normalizeRole = (role: string): string => {
  return BACKEND_ROLE_MAP[role] || role;
};

// Loading Animation Component
const LoadingAnimation: React.FC = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="loading-screen"
  >
    <div className="neon-container">
      <motion.div
        className="neon-logo"
        animate={{
          textShadow: [
            '0 0 10px #ff006e, 0 0 20px #ff006e, 0 0 30px #ff006e',
            '0 0 20px #8b5cf6, 0 0 30px #8b5cf6, 0 0 40px #8b5cf6',
            '0 0 10px #ff006e, 0 0 20px #ff006e, 0 0 30px #ff006e',
          ],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        NEXUS
      </motion.div>
      <motion.div
        className="loading-bars"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
      <p className="loading-text">Initializing System...</p>
    </div>
  </motion.div>
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const setSession = (newToken: string, rawUser: Partial<User>, markJustLoggedIn: boolean = true) => {
    const normalizedUser: User = {
      id: rawUser.id || '',
      username: rawUser.username || '',
      email: rawUser.email || '',
      role: normalizeRole((rawUser.role || 'STUDENT') as string) as User['role'],
      avatar: rawUser.avatar,
    };

    setToken(newToken);
    setUser(normalizedUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('role', normalizedUser.role);
    if (normalizedUser.id) {
      localStorage.setItem('userId', normalizedUser.id);
    }
    if (markJustLoggedIn) {
      localStorage.setItem('justLoggedIn', 'true');
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedRole = localStorage.getItem('role');
      
      if (storedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await axios.get(`${API_URL}/api/auth/me`, { timeout: 10000 });
          const userData = { ...response.data, role: normalizeRole(response.data.role || storedRole || 'STUDENT') };
          setSession(storedToken, userData, false);
          // Update localStorage with normalized role if different
          if (userData.role !== storedRole) {
            localStorage.setItem('role', userData.role);
          }
        } catch (error: any) {
          localStorage.removeItem('token');
          localStorage.removeItem('role');
          localStorage.removeItem('userId');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        }
      }
      
      // Show loading animation for minimum 1.5 seconds for smooth UX
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 1500));
      await minLoadTime;
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (username: string, password: string, role: string = 'student') => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { username, password, role });
    setSession(response.data.token, response.data.user, true);
  };

  const register = async (username: string, email: string, password: string, role: string) => {
    let endpoint = '/register/student';
    if (role === 'club_admin') endpoint = '/register/club-admin';
    if (role === 'super_admin') endpoint = '/register/admin';
    const response = await axios.post(`${API_URL}/api/auth${endpoint}`, {
      username,
      password,
      email,
    });
    setSession(response.data.token, response.data.user, true);
  };

  const setAuthSession = (newToken: string, authUser: Partial<User>) => {
    setSession(newToken, authUser, true);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userId');
    localStorage.removeItem('justLoggedIn');
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateAvatar = (avatar: string) => {
    if (user) {
      const updatedUser = { ...user, avatar };
      setUser(updatedUser);
    }
  };

  if (loading) return <LoadingAnimation />;

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, updateAvatar, setAuthSession }}>
      {children}
    </AuthContext.Provider>
  );
};
